import { AxiosError } from 'axios';

import { getEmptyOffsetPagedResults, OffsetPagedResults } from '~interfaces';
import ResourceGroup from '~interfaces/resourceGroup';
import { SkcOffsetPagedResponse } from '~interfaces/responses';
import i18n from '~lib/i18n';
import ApiError from '~services/apiError';
import SkopeiConnectService from '~services/skopeiConnectService';

class ResourceGroupService extends SkopeiConnectService {
	protected readonly path = 'resource-groups';

	/**
	 * Gets the list of resource groups.
	 *
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param searchQuery Text to search for in the device name, hardware id or bluetooth name.
	 * @param overwrittenClientId Filter on organisation id.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns An array of resourceGroup.
	 */
	async getResourceGroups(params: {
		page: string;
		pageSize: string;
		searchQuery?: string;
		overwrittenClientId?: string;
		signal?: AbortSignal;
	}): Promise<OffsetPagedResults<ResourceGroup>> {
		const endpoint = this.createEndpoint('v2');
		return await this.client
			.get<SkcOffsetPagedResponse<ResourceGroupResponse>>(endpoint, {
				signal: params.signal,
				params: {
					'page-number': params.page,
					'page-size': params.pageSize,
					search: params.searchQuery?.trim() || undefined,
				},
				headers: {
					OverwrittenClientId: params.overwrittenClientId,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.resolve(
						getEmptyOffsetPagedResults<ResourceGroup>(Number(params.page), Number(params.pageSize))
					);
				}
				return Promise.resolve(
					this.mapSkcOffsetPagedResponse(response.data, ResourceGroupService.fromResponse)
				);
			});
	}

	/**
	 * Gets the list of resource groups from the internal endpoint.
	 *
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param searchQuery Text to search for in the device name, hardware id or bluetooth name.
	 * @param organisationId Filter on organisation id.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns An array of resourceGroup.
	 */
	async getResourceGroupsInternal(
		params: {
			page: string;
			pageSize: string;
			searchQuery?: string;
			organisationId?: string;
		},
		signal?: AbortSignal
	): Promise<OffsetPagedResults<ResourceGroup>> {
		const endpoint = this.createEndpoint('v2', [], true);
		return await this.client
			.get<SkcOffsetPagedResponse<ResourceGroupResponse>>(endpoint, {
				signal: signal,
				params: {
					'page-number': params.page,
					'page-size': params.pageSize,
					search: params.searchQuery?.trim() || undefined,
					'client-id': params.organisationId,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.resolve(
						getEmptyOffsetPagedResults<ResourceGroup>(Number(params.page), Number(params.pageSize))
					);
				}
				return Promise.resolve(
					this.mapSkcOffsetPagedResponse(response.data, ResourceGroupService.fromResponse)
				);
			});
	}

	public getResourceGroupsCacheKey(searchParams: URLSearchParams, overwrittenClientId?: string) {
		return [this.path, searchParams.toString(), overwrittenClientId] as const;
	}

	/**
	 * Gets a resource group by its id.
	 *
	 * @param resourceGroupId The id of the resource group.
	 * @param organisationId  The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns The resource group.
	 */
	async getResourceGroupById(
		resourceGroupId: string,
		organisationId?: string,
		signal?: AbortSignal
	): Promise<ResourceGroup | undefined> {
		const endpoint = this.createEndpoint('v2', [resourceGroupId]);
		return await this.client
			.get<ResourceGroupResponse>(endpoint, {
				signal: signal,
				headers:
					organisationId != undefined ?
						{
							OverwrittenClientId: organisationId,
						}
					:	undefined,
			})
			.then((response) => {
				if (!response || response.data == undefined) {
					const deviceNotFoundError = new ApiError(new AxiosError(''));
					deviceNotFoundError.setDescription(
						i18n.t('server.resourceGroupNotFoundException', { ns: 'errors' })
					);
					return Promise.reject(deviceNotFoundError);
				}

				return Promise.resolve(ResourceGroupService.fromResponse(response.data));
			})
			.catch((error: ApiError) => {
				if (error.errorCode == 'SKC_004') {
					error.setDescription(i18n.t('server.resourceGroupNotFoundException', { ns: 'errors' }));
				}
				return Promise.reject(error);
			});
	}

	public getResourceGroupByIdCacheKey(resourceGroupId?: string, overwrittenClientId?: string) {
		return [this.path, resourceGroupId, overwrittenClientId] as const;
	}

	/**
	 * Assigns devices to a resource group.
	 *
	 * @param deviceIds The list of device ids to be assigned.
	 * @param resourceGroupId The resource group the devices are to be assigned to.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 */
	async assignDevicesToResourceGroup(
		deviceIds: string[],
		resourceGroupId: string,
		organisationId?: string,
		signal?: AbortSignal
	) {
		const endpoint = this.createEndpoint('v2', [resourceGroupId, 'assign-devices']);
		await this.client.post<string>(endpoint, deviceIds, {
			signal: signal,
			headers: {
				OverwrittenClientId: organisationId,
			},
		});
	}

	/**
	 * Assigns keys to a resource group.
	 *
	 * @param keyIds The list of key ids to be assigned.
	 * @param resourceGroupId The resource group the keys are to be assigned to.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 */
	async assignKeysToResourceGroup(
		keyIds: string[],
		resourceGroupId: string,
		organisationId?: string,
		signal?: AbortSignal
	) {
		const endpoint = this.createEndpoint('v2', [resourceGroupId, 'assign-keys']);
		await this.client.post<string>(endpoint, keyIds, {
			signal: signal,
			headers: {
				OverwrittenClientId: organisationId,
			},
		});
	}

	/**
	 * Model parsing.
	 */
	static fromResponse(response: ResourceGroupResponse): ResourceGroup {
		return {
			id: response.id.toString(),
			organisationId: response.clientId?.toString(),
			name: response.name,
		};
	}
}

interface ResourceGroupResponse {
	id: number;
	clientId?: number;
	name: string;
}

export default ResourceGroupService;
